import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, List, ListItem, ListItemIcon, ListItemText, MenuItem,
  Select, Snackbar, Alert, TextField, Typography,
  CircularProgress, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LinkIcon from '@mui/icons-material/Link';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  TutorTopicResourceDTO,
  listResources,
  addResource,
  uploadFile,
  deleteResource,
  reorderResources
} from '../../../services/tutorTopicResourceService';

interface Props {
  courseId: number;
  nodeId: number;
}

const ResourceIcon = ({ type }: { type: string }) => {
  if (type === 'YOUTUBE') return <VideoLibraryIcon color="error" />;
  if (type === 'FILE') return <InsertDriveFileIcon color="primary" />;
  return <LinkIcon color="action" />;
};

export default function ExtraResourcesPanel({ courseId, nodeId }: Props) {
  const [resources, setResources] = useState<TutorTopicResourceDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resourceType, setResourceType] = useState<'URL' | 'YOUTUBE'>('URL');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    listResources(courseId, nodeId)
      .then(setResources)
      .catch(() => setError('Failed to load resources'))
      .finally(() => setLoading(false));
  }, [courseId, nodeId]);

  const handleAddUrlYoutube = async () => {
    if (!url.trim()) return;
    setAdding(true);
    try {
      const created = await addResource(courseId, nodeId, resourceType, title || url, url);
      setResources(prev => [...prev, created]);
      setDialogOpen(false);
      setTitle('');
      setUrl('');
    } catch {
      setError('Failed to add resource');
    } finally {
      setAdding(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const created = await uploadFile(courseId, nodeId, file);
      setResources(prev => [...prev, created]);
    } catch {
      setError('Failed to upload file. Only PDF, Word, and PowerPoint files up to 10 MB are allowed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteResource(courseId, nodeId, id);
      setResources(prev => prev.filter(r => r.id !== id));
    } catch {
      setError('Failed to delete resource');
    }
    setDeleteConfirmId(null);
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const newList = [...resources];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    setResources(newList);
    try {
      await reorderResources(courseId, nodeId, newList.map(r => r.id));
    } catch {
      setError('Failed to reorder resources');
      setResources(resources);
    }
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box mt={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" fontWeight="bold">Extra Resources</Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Upload file (PDF, Word, PowerPoint)">
            <span>
              <Button
                component="label"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                size="small"
                disabled={uploading}
              >
                Upload
                <input type="file" hidden accept=".pdf,.doc,.docx,.ppt,.pptx" onChange={handleFileUpload} />
              </Button>
            </span>
          </Tooltip>
          <Button startIcon={<AddIcon />} size="small" onClick={() => setDialogOpen(true)}>
            Add Link
          </Button>
        </Box>
      </Box>

      {resources.length === 0 && (
        <Typography variant="body2" color="text.secondary">No extra resources yet.</Typography>
      )}

      <List dense disablePadding>
        {resources.map((r, i) => (
          <ListItem
            key={r.id}
            disableGutters
            secondaryAction={
              <Box display="flex" gap={0.5}>
                <IconButton size="small" disabled={i === 0} onClick={() => handleMove(i, 'up')}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={i === resources.length - 1} onClick={() => handleMove(i, 'down')}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(r.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ResourceIcon type={r.resourceType} />
            </ListItemIcon>
            <ListItemText primary={r.title || r.originalFilename || r.url} />
          </ListItem>
        ))}
      </List>

      {/* Add URL/YouTube dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Resource Link</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Select
            value={resourceType}
            onChange={e => setResourceType(e.target.value as 'URL' | 'YOUTUBE')}
            size="small"
          >
            <MenuItem value="URL">Web Link</MenuItem>
            <MenuItem value="YOUTUBE">YouTube Video</MenuItem>
          </Select>
          <TextField
            label="Title (optional)"
            value={title}
            onChange={e => setTitle(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="URL"
            value={url}
            onChange={e => setUrl(e.target.value)}
            size="small"
            fullWidth
            required
            placeholder={resourceType === 'YOUTUBE' ? 'https://www.youtube.com/watch?v=...' : 'https://'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddUrlYoutube} variant="contained" disabled={adding || !url.trim()}>
            {adding ? <CircularProgress size={16} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Resource?</DialogTitle>
        <DialogContent>
          <Typography>This resource will be permanently removed.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}
